/**
 * Energieausweis sub-section — row cells, summary and table for kind `Energieausweis`.
 * Shared atoms (`taPill`, `taTag`, dates, numbers) come from `technicalAssetsSection.render.js`;
 * all cross-file references are inside function bodies, so gulp concat order does not matter.
 */

/** German labels for `typeFields.certificateType` of kind `Energieausweis` (Lux kind catalog). */
function energyCertificateTypeLabel(value) {
  const labels = {
    ENERGY_DEMAND: 'Bedarfsausweis',
    ENERGY_CONSUMPTION: 'Verbrauchsausweis',
  }
  return labels[String(value)] || ''
}

function energyCertificateClassLabel(value) {
  if (!value) return ''
  return value === 'A_PLUS' ? 'A+' : String(value)
}

/** Validity from `validUntil`: expired → danger, < 6 months → warn, else ok; no date → muted. */
function energyCertificateValidity(tf) {
  const days = technicalAssetDaysUntil(tf.validUntil)
  if (days == null) return { label: 'Gültigkeit unbekannt', tone: 'muted' }
  if (days < 0) return { label: 'Abgelaufen', tone: 'danger' }
  if (days <= 182) return { label: 'Läuft bald ab', tone: 'warn' }
  return { label: 'Gültig', tone: 'ok' }
}

function energyCertificateTypeFields(a) {
  return (
    (a && a.typeFields && typeof a.typeFields === 'object' && a.typeFields) ||
    {}
  )
}

function renderEnergyCertificateNameCell(a, ctx) {
  const tf = energyCertificateTypeFields(a)
  const validity = energyCertificateValidity(tf)
  const issueYear = String(tf.issueDate || '').slice(0, 4)
  const sub = [
    tf.heatingAreaSqm != null
      ? `Beheizte Fläche ${taFormatNumber(tf.heatingAreaSqm, 1)} m²`
      : '',
    tf.issueDate ? `Ausgestellt ${isoDateToGermanDisplay(tf.issueDate)}` : '',
  ]
    .filter(Boolean)
    .join(' · ')
  return `
    <div class="impowerx-ta-titleline">
      ${TECHNICAL_ASSET_ROW_ICONS.file}
      <span class="impowerx-ta-primary"><a href="${escapeHtml(ctx.detailHref(a))}">${escapeHtml(a.name || '—')}</a></span>
      ${taTag(issueYear ? `EA-${issueYear}` : `EA-${a.id}`)}
      ${taPill(validity.label, validity.tone)}
    </div>
    ${taSecondary(sub)}`
}

function renderEnergyCertificateEfficiencyCell(a) {
  const tf = energyCertificateTypeFields(a)
  const type = energyCertificateTypeLabel(tf.certificateType)
  const cls = energyCertificateClassLabel(tf.energyClass)
  const pills = [
    type ? taPill(type, 'light') : '',
    cls ? taPill(`Klasse ${cls}`, 'dark') : '',
  ]
    .filter(Boolean)
    .join(' ')
  const parts = []
  if (pills) parts.push(`<div class="impowerx-ta-titleline">${pills}</div>`)
  if (tf.finalEnergyDemandKwh != null) {
    parts.push(
      `<div class="impowerx-ta-strong" style="margin-top:4px">${taFormatNumber(tf.finalEnergyDemandKwh, 1)} kWh/(m²·a) Endenergie</div>`
    )
  }
  if (tf.primaryEnergyDemandKwh != null) {
    parts.push(
      taSecondary(
        `Primärenergie: ${taFormatNumber(tf.primaryEnergyDemandKwh, 1)} kWh/(m²·a)`
      )
    )
  }
  return parts.length
    ? parts.join('')
    : '<span class="impowerx-ta-secondary">—</span>'
}

function renderEnergyCertificateIssuerCell(a, ctx) {
  const tf = energyCertificateTypeFields(a)
  const validity = energyCertificateValidity(tf)
  const parts = []
  if (tf.issuingCompanyContactId != null) {
    parts.push(
      taLine(
        'building',
        `<span class="impowerx-ta-strong">${escapeHtml(taContactName(ctx.contactNames, tf.issuingCompanyContactId))}</span>`
      )
    )
  }
  if (tf.issueDate) {
    parts.push(
      taSecondary(`Ausgestellt: ${isoDateToGermanDisplay(tf.issueDate)}`)
    )
  }
  if (tf.validUntil) {
    parts.push(
      `<div style="margin-top:4px">${taPill(`Gültig bis ${isoDateToGermanDisplay(tf.validUntil)}`, validity.tone)}</div>`
    )
  }
  return parts.length
    ? parts.join('')
    : '<span class="impowerx-ta-secondary">—</span>'
}

function renderEnergyCertificatesSummaryHtml(rows) {
  const valid = rows.filter((a) => {
    const tone = energyCertificateValidity(energyCertificateTypeFields(a)).tone
    return tone === 'ok' || tone === 'warn'
  }).length
  const next = rows
    .map((a) => energyCertificateTypeFields(a).validUntil)
    .filter(Boolean)
    .sort()[0]
  const left = `${rows.length} ${rows.length === 1 ? 'Energieausweis' : 'Energieausweise'} erfasst · ${valid} gültig`
  const right = next
    ? `Nächste Neuausstellung erforderlich: <b>${isoDateToGermanDisplay(next)}</b>`
    : ''
  return `<div class="impowerx-ta-summary"><span class="impowerx-ta-summary-left">${TECHNICAL_ASSET_ROW_ICONS.check}${escapeHtml(left)}</span><span>${right}</span></div>`
}

function renderEnergyCertificatesTableHtml(rows, ctx) {
  return renderTechnicalAssetsTableShellHtml({
    columns: [
      {
        key: 'name',
        label: 'Name / Kennung',
        width: 23,
        cell: renderEnergyCertificateNameCell,
        sortValue: (a) => a.name,
      },
      {
        key: 'building',
        label: 'Gebäude',
        width: 16,
        cell: renderTechnicalAssetBuildingCell,
        sortValue: (a) => a.buildingLabel,
      },
      {
        key: 'efficiency',
        label: 'Typ & Effizienz',
        width: 20,
        cell: renderEnergyCertificateEfficiencyCell,
        sortValue: (a) =>
          energyCertificateClassLabel(
            energyCertificateTypeFields(a).energyClass
          ) || null,
      },
      {
        key: 'validity',
        label: 'Ausstellende Firma & Gültigkeit',
        width: 20,
        cell: renderEnergyCertificateIssuerCell,
        sortValue: (a) => energyCertificateTypeFields(a).validUntil || null,
      },
      {
        key: 'documents',
        label: 'Dokumente',
        width: 11,
        cell: renderTechnicalAssetDocumentsCell,
        sortValue: (a) => (a.documents || []).length,
      },
    ],
    rows,
    emptyText: 'Kein Energieausweis erfasst.',
    deleteAriaLabel: 'Energieausweis löschen',
    summaryHtml: renderEnergyCertificatesSummaryHtml(ctx.allRows || rows),
    ctx,
  })
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    energyCertificateValidity,
    renderEnergyCertificatesSummaryHtml,
  }
}
